import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Leaf,
  Plus,
  Edit,
  Trash2,
  Users,
  UserPlus,
  LogOut,
  Shield,
  X,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Crown,
  Video,
  Phone,
  Mail,
  Award
} from "lucide-react";

const COACHING_CATEGORIES = [
  "Professional Development",
  "Entrepreneurial Development",
  "Financial Planning",
  "Communication Skills",
  "Organizational Leadership",
  "Writing Skills",
  "Personal Growth",
  "Work-Life Balance",
  "Career Transition",
  "Public Speaking"
];

const COACHING_STYLES = [
  "Supportive & Encouraging",
  "Direct & Challenging",
  "Structured & Goal-Oriented",
  "Intuitive & Exploratory",
  "Analytical & Data-Driven"
];

const initialFormState = {
  first_name: "",
  last_name: "",
  email: "",
  gender: "",
  bio: "",
  specializations: [],
  experience_years: 0,
  coaching_style: [],
  availability: "",
  photo_url: "",
  hourly_rate: 0
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [preferredApplications, setPreferredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  const [preferredDialogOpen, setPreferredDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedPreferredApp, setSelectedPreferredApp] = useState(null);
  const [editingCoach, setEditingCoach] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [activeTab, setActiveTab] = useState("coaches");
  const [preferredNotes, setPreferredNotes] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coachesRes, usersRes, applicationsRes, preferredRes] = await Promise.all([
        axios.get(`${API}/coaches`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/coach-applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/preferred-applications`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setCoaches(coachesRes.data);
      setUsers(usersRes.data);
      setApplications(applicationsRes.data);
      setPreferredApplications(preferredRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const openCreateDialog = () => {
    setEditingCoach(null);
    setFormData(initialFormState);
    setDialogOpen(true);
  };

  const openEditDialog = (coach) => {
    setEditingCoach(coach);
    setFormData({
      first_name: coach.first_name,
      last_name: coach.last_name,
      email: coach.email,
      gender: coach.gender,
      bio: coach.bio,
      specializations: coach.specializations,
      experience_years: coach.experience_years,
      coaching_style: coach.coaching_style,
      availability: coach.availability,
      photo_url: coach.photo_url || "",
      hourly_rate: coach.hourly_rate || 0
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCoach) {
        await axios.put(
          `${API}/admin/coaches/${editingCoach.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Coach updated successfully");
      } else {
        await axios.post(
          `${API}/admin/coaches`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Coach created successfully");
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save coach");
    }
  };

  const handleDelete = async (coachId) => {
    if (!window.confirm("Are you sure you want to delete this coach?")) return;

    try {
      await axios.delete(`${API}/admin/coaches/${coachId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Coach deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete coach");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete ${userName}'s account? This removes their survey responses and match history. This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleTogglePreferred = async (coachId, newValue) => {
    try {
      await axios.patch(
        `${API}/admin/coaches/${coachId}/preferred`,
        { is_preferred: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newValue ? "Coach marked as Preferred" : "Preferred status removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to update preferred status");
    }
  };

  const toggleSpecialization = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const toggleCoachingStyle = (style) => {
    setFormData(prev => ({
      ...prev,
      coaching_style: prev.coaching_style.includes(style)
        ? prev.coaching_style.filter(s => s !== style)
        : [...prev.coaching_style, style]
    }));
  };

  const viewApplication = (app) => {
    setSelectedApplication(app);
    setApplicationDialogOpen(true);
  };

  const updateApplicationStatus = async (appId, status) => {
    try {
      await axios.put(
        `${API}/admin/coach-applications/${appId}/status?new_status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Application ${status}`);
      fetchData();
      setApplicationDialogOpen(false);
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error(error.response?.data?.detail || "Failed to update application status");
    }
  };

  const deleteApplication = async (appId) => {
    if (!window.confirm("Are you sure you want to delete this application?")) return;
    
    try {
      await axios.delete(`${API}/admin/coach-applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Application deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete application");
    }
  };

  const viewPreferredApplication = (app) => {
    setSelectedPreferredApp(app);
    setPreferredNotes("");
    setPreferredDialogOpen(true);
  };

  const updatePreferredStatus = async (appId, status) => {
    try {
      await axios.put(
        `${API}/admin/preferred-applications/${appId}/status?new_status=${status}${preferredNotes ? `&notes=${encodeURIComponent(preferredNotes)}` : ""}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Preferred application ${status.replace(/_/g, " ")}`);
      fetchData();
      setPreferredDialogOpen(false);
    } catch (error) {
      console.error("Error updating preferred application:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const convertApplicationToCoach = async (app) => {
    const data = app.application_data;
    
    // Create coach data from application
    const coachData = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      gender: data.gender || "Not specified",
      bio: data.bio || data.coaching_background || `${data.first_name} is an experienced ministry coach.`,
      specializations: data.subjects_coached || [],
      experience_years: data.years_coaching || 0,
      coaching_style: [],
      availability: data.availability_hours || "Flexible",
      photo_url: data.photo_url || "",
      hourly_rate: 0,
      enneagram: data.enneagram || "",
      myers_briggs: data.myers_briggs || "",
      strengths_finder: data.strengths_finder || [],
      primal_question: data.primal_question || ""
    };
    
    try {
      // Create the coach directly
      await axios.post(
        `${API}/admin/coaches`,
        coachData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update application status to approved
      await axios.put(
        `${API}/admin/coach-applications/${app.id}/status?new_status=approved`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${data.first_name} ${data.last_name} has been added as a coach!`);
      setApplicationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add coach");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-[#7C9A92]" />
            <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-[#78716C] hover:text-[#2C332B] font-medium">Dashboard</Link>
            <Link to="/coaches" className="text-[#78716C] hover:text-[#2C332B] font-medium">Coaches</Link>
            <Link to="/admin" className="text-[#7C9A92] font-medium flex items-center gap-1">
              <Shield className="w-4 h-4" />
              Admin
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            <Badge className="bg-[#E09F7D] text-white">Admin</Badge>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-[#78716C] hover:text-[#2C332B]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2C332B] mb-4">
            Admin Dashboard
          </h1>
          <p className="text-lg text-[#78716C]">
            Manage coaches and view user registrations
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab("coaches")}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#7C9A92]/10 flex items-center justify-center">
                  <UserPlus className="w-7 h-7 text-[#7C9A92]" />
                </div>
                <div>
                  <p className="text-3xl font-semibold text-[#2C332B]">{coaches.length}</p>
                  <p className="text-[#78716C]">Coaches</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab("applications")}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#E09F7D]/10 flex items-center justify-center">
                  <FileText className="w-7 h-7 text-[#E09F7D]" />
                </div>
                <div>
                  <p className="text-3xl font-semibold text-[#2C332B]">{applications.length}</p>
                  <p className="text-[#78716C]">Coach Applications</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab("users")}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#7C9A92]/10 flex items-center justify-center">
                  <Users className="w-7 h-7 text-[#7C9A92]" />
                </div>
                <div>
                  <p className="text-3xl font-semibold text-[#2C332B]">{users.length}</p>
                  <p className="text-[#78716C]">Registered Users</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab("preferred")}>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#E09F7D]/10 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-[#E09F7D]" />
                </div>
                <div>
                  <p className="text-3xl font-semibold text-[#2C332B]">{preferredApplications.length}</p>
                  <p className="text-[#78716C]">Preferred Apps</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <Button
            variant={activeTab === "coaches" ? "default" : "outline"}
            onClick={() => setActiveTab("coaches")}
            className={`rounded-full ${activeTab === "coaches" ? "bg-[#7C9A92] hover:bg-[#6B8A82]" : ""}`}
            data-testid="coaches-tab"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Coaches
          </Button>
          <Button
            variant={activeTab === "applications" ? "default" : "outline"}
            onClick={() => setActiveTab("applications")}
            className={`rounded-full ${activeTab === "applications" ? "bg-[#7C9A92] hover:bg-[#6B8A82]" : ""}`}
            data-testid="applications-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            Applications
            {applications.filter(a => a.status === "pending").length > 0 && (
              <span className="ml-2 bg-[#E09F7D] text-white text-xs px-2 py-0.5 rounded-full">
                {applications.filter(a => a.status === "pending").length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === "preferred" ? "default" : "outline"}
            onClick={() => setActiveTab("preferred")}
            className={`rounded-full ${activeTab === "preferred" ? "bg-[#7C9A92] hover:bg-[#6B8A82]" : ""}`}
            data-testid="preferred-tab"
          >
            <Crown className="w-4 h-4 mr-2" />
            Preferred
            {preferredApplications.filter(a => a.status === "pending_review" || a.status === "interview_requested").length > 0 && (
              <span className="ml-2 bg-[#E09F7D] text-white text-xs px-2 py-0.5 rounded-full">
                {preferredApplications.filter(a => a.status === "pending_review" || a.status === "interview_requested").length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === "users" ? "default" : "outline"}
            onClick={() => setActiveTab("users")}
            className={`rounded-full ${activeTab === "users" ? "bg-[#7C9A92] hover:bg-[#6B8A82]" : ""}`}
            data-testid="users-tab"
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </Button>
        </div>

        {/* Coaches Tab */}
        {activeTab === "coaches" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif text-xl">Manage Coaches</CardTitle>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={openCreateDialog}
                      className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                      data-testid="add-coach-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Coach
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-2xl">
                        {editingCoach ? "Edit Coach" : "Add New Coach"}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>First Name</Label>
                          <Input
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            className="rounded-xl"
                            required
                            data-testid="coach-first-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Last Name</Label>
                          <Input
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            className="rounded-xl"
                            required
                            data-testid="coach-last-name"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="rounded-xl"
                            required
                            data-testid="coach-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Gender</Label>
                          <Select
                            value={formData.gender}
                            onValueChange={(v) => setFormData({ ...formData, gender: v })}
                          >
                            <SelectTrigger className="rounded-xl" data-testid="coach-gender">
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Male">Male</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Bio</Label>
                        <Textarea
                          value={formData.bio}
                          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                          className="rounded-xl min-h-[100px]"
                          required
                          data-testid="coach-bio"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Specializations</Label>
                        <div className="flex flex-wrap gap-2">
                          {COACHING_CATEGORIES.map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => toggleSpecialization(cat)}
                              className={`px-3 py-1 rounded-full text-sm transition-all ${
                                formData.specializations.includes(cat)
                                  ? "bg-[#7C9A92] text-white"
                                  : "bg-stone-100 text-[#2C332B] hover:bg-stone-200"
                              }`}
                            >
                              {cat}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Experience (years)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.experience_years}
                            onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) || 0 })}
                            className="rounded-xl"
                            required
                            data-testid="coach-experience"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Hourly Rate ($)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.hourly_rate}
                            onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                            className="rounded-xl"
                            data-testid="coach-rate"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Coaching Style</Label>
                        <div className="flex flex-wrap gap-2">
                          {COACHING_STYLES.map((style) => (
                            <button
                              key={style}
                              type="button"
                              onClick={() => toggleCoachingStyle(style)}
                              className={`px-3 py-1 rounded-full text-sm transition-all ${
                                formData.coaching_style.includes(style)
                                  ? "bg-[#E09F7D] text-white"
                                  : "bg-stone-100 text-[#2C332B] hover:bg-stone-200"
                              }`}
                            >
                              {style}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Availability</Label>
                          <Select
                            value={formData.availability}
                            onValueChange={(v) => setFormData({ ...formData, availability: v })}
                          >
                            <SelectTrigger className="rounded-xl" data-testid="coach-availability">
                              <SelectValue placeholder="Select availability" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Weekday mornings">Weekday mornings</SelectItem>
                              <SelectItem value="Weekday afternoons">Weekday afternoons</SelectItem>
                              <SelectItem value="Weekday evenings">Weekday evenings</SelectItem>
                              <SelectItem value="Weekends">Weekends</SelectItem>
                              <SelectItem value="Flexible">Flexible</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Photo URL (optional)</Label>
                          <Input
                            value={formData.photo_url}
                            onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                            className="rounded-xl"
                            placeholder="https://..."
                            data-testid="coach-photo"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-4 pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDialogOpen(false)}
                          className="rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                          data-testid="save-coach-btn"
                        >
                          {editingCoach ? "Update Coach" : "Create Coach"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {coaches.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Specializations</TableHead>
                        <TableHead>Experience</TableHead>
                        <TableHead>Preferred</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coaches.map((coach) => (
                        <TableRow key={coach.id}>
                          <TableCell className="font-medium">
                            {coach.first_name} {coach.last_name}
                          </TableCell>
                          <TableCell>{coach.email}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {coach.specializations.slice(0, 2).map((spec) => (
                                <Badge key={spec} variant="secondary" className="text-xs bg-[#F5F1E8]">
                                  {spec}
                                </Badge>
                              ))}
                              {coach.specializations.length > 2 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{coach.specializations.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{coach.experience_years} years</TableCell>
                          <TableCell>
                            <Switch
                              checked={!!coach.is_preferred}
                              onCheckedChange={(checked) => handleTogglePreferred(coach.id, checked)}
                              data-testid={`preferred-toggle-${coach.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(coach)}
                                data-testid={`edit-coach-${coach.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(coach.id)}
                                className="text-red-500 hover:text-red-600"
                                data-testid={`delete-coach-${coach.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-[#78716C]">
                    <UserPlus className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>No coaches added yet. Click "Add Coach" to get started.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl">Registered Users</CardTitle>
              </CardHeader>
              <CardContent>
                {users.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Survey</TableHead>
                        <TableHead>Admin</TableHead>
                        <TableHead>Registered</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">
                            {u.first_name} {u.last_name}
                          </TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Badge
                              variant={u.survey_completed ? "default" : "secondary"}
                              className={u.survey_completed ? "bg-[#7C9A92]" : ""}
                            >
                              {u.survey_completed ? "Completed" : "Pending"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {u.is_admin && (
                              <Badge className="bg-[#E09F7D]">Admin</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {new Date(u.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id, `${u.first_name} ${u.last_name}`)}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`delete-user-${u.id}`}
                              disabled={u.is_admin}
                              title={u.is_admin ? "Admins cannot be deleted from here" : "Delete user"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-[#78716C]">
                    <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>No users registered yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Applications Tab */}
        {activeTab === "applications" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl">Coach Applications</CardTitle>
              </CardHeader>
              <CardContent>
                {applications.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Experience</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applications.map((app) => (
                        <TableRow key={app.id}>
                          <TableCell className="font-medium">
                            {app.application_data.first_name} {app.application_data.last_name}
                          </TableCell>
                          <TableCell>{app.application_data.email}</TableCell>
                          <TableCell>{app.application_data.years_coaching || 0} years</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                app.status === "approved" ? "bg-[#7C9A92]" :
                                app.status === "rejected" ? "bg-red-500" :
                                "bg-[#E09F7D]"
                              }
                            >
                              {app.status === "pending" && <Clock className="w-3 h-3 mr-1" />}
                              {app.status === "approved" && <CheckCircle className="w-3 h-3 mr-1" />}
                              {app.status === "rejected" && <XCircle className="w-3 h-3 mr-1" />}
                              {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(app.submitted_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewApplication(app)}
                                data-testid={`view-app-${app.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteApplication(app.id)}
                                className="text-red-500 hover:text-red-600"
                                data-testid={`delete-app-${app.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-[#78716C]">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>No coach applications yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Application Review Dialog */}
        <Dialog open={applicationDialogOpen} onOpenChange={setApplicationDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                Review Application
              </DialogTitle>
            </DialogHeader>
            {selectedApplication && (
              <div className="space-y-6 mt-4">
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <Badge
                    className={
                      selectedApplication.status === "approved" ? "bg-[#7C9A92]" :
                      selectedApplication.status === "rejected" ? "bg-red-500" :
                      "bg-[#E09F7D]"
                    }
                  >
                    {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                  </Badge>
                  <span className="text-sm text-[#78716C]">
                    Submitted: {new Date(selectedApplication.submitted_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Personal Info */}
                <div className="bg-[#F5F1E8] rounded-xl p-4">
                  <h3 className="font-medium text-[#2C332B] mb-3">Personal Information</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#78716C]">Name:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.first_name} {selectedApplication.application_data.last_name}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Email:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.email}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Phone:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.phone || "Not provided"}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Location:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.location || "Not provided"}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Gender:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.gender || "Not provided"}</span>
                    </div>
                  </div>
                </div>

                {/* Experience */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h3 className="font-medium text-[#2C332B] mb-3">Experience</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-[#78716C]">Years Coaching:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.years_coaching || 0}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Clients Coached:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.total_clients_coached || "Not provided"}</span>
                    </div>
                  </div>
                  {selectedApplication.application_data.coaching_background && (
                    <div>
                      <span className="text-[#78716C] text-sm">Background:</span>
                      <p className="mt-1 text-sm">{selectedApplication.application_data.coaching_background}</p>
                    </div>
                  )}
                </div>

                {/* Specializations */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h3 className="font-medium text-[#2C332B] mb-3">Specializations</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedApplication.application_data.subjects_coached?.map((subject) => (
                      <Badge key={subject} variant="secondary" className="bg-[#F5F1E8]">{subject}</Badge>
                    ))}
                  </div>
                  <div className="text-sm">
                    <span className="text-[#78716C]">Industries:</span>
                    <span className="ml-2">{selectedApplication.application_data.industries_served?.join(", ") || "None specified"}</span>
                  </div>
                </div>

                {/* Certifications */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h3 className="font-medium text-[#2C332B] mb-3">Certifications</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedApplication.application_data.certifications?.map((cert) => (
                      <Badge key={cert} className="bg-[#7C9A92]">{cert}</Badge>
                    ))}
                    {(!selectedApplication.application_data.certifications || selectedApplication.application_data.certifications.length === 0) && (
                      <span className="text-sm text-[#78716C]">No certifications selected</span>
                    )}
                  </div>
                  {selectedApplication.application_data.other_certifications && (
                    <div className="text-sm">
                      <span className="text-[#78716C]">Other:</span>
                      <span className="ml-2">{selectedApplication.application_data.other_certifications}</span>
                    </div>
                  )}
                </div>

                {/* Availability */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h3 className="font-medium text-[#2C332B] mb-3">Availability</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#78716C]">Hours/Week:</span>
                      <span className="ml-2 font-medium">{selectedApplication.application_data.availability_hours || "Not specified"}</span>
                    </div>
                    <div>
                      <span className="text-[#78716C]">Days:</span>
                      <span className="ml-2">{selectedApplication.application_data.availability_days?.join(", ") || "Not specified"}</span>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-[#78716C]">Session Formats:</span>
                      <span className="ml-2">{selectedApplication.application_data.session_formats?.join(", ") || "Not specified"}</span>
                    </div>
                  </div>
                </div>

                {/* Links */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h3 className="font-medium text-[#2C332B] mb-3">Links & Media</h3>
                  <div className="space-y-2 text-sm">
                    {selectedApplication.application_data.website_url && (
                      <div>
                        <span className="text-[#78716C]">Website:</span>
                        <a href={selectedApplication.application_data.website_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-[#7C9A92] hover:underline">
                          {selectedApplication.application_data.website_url}
                        </a>
                      </div>
                    )}
                    {selectedApplication.application_data.linkedin_url && (
                      <div>
                        <span className="text-[#78716C]">LinkedIn:</span>
                        <a href={selectedApplication.application_data.linkedin_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-[#7C9A92] hover:underline">
                          {selectedApplication.application_data.linkedin_url}
                        </a>
                      </div>
                    )}
                    {selectedApplication.application_data.photo_url && (
                      <div>
                        <span className="text-[#78716C]">Photo:</span>
                        <a href={selectedApplication.application_data.photo_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-[#7C9A92] hover:underline">
                          View Photo
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {selectedApplication.application_data.bio && (
                  <div className="bg-white rounded-xl p-4 border border-stone-100">
                    <h3 className="font-medium text-[#2C332B] mb-3">Bio</h3>
                    <p className="text-sm">{selectedApplication.application_data.bio}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t">
                  {selectedApplication.status === "pending" && (
                    <>
                      <Button
                        onClick={() => updateApplicationStatus(selectedApplication.id, "approved")}
                        className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => updateApplicationStatus(selectedApplication.id, "rejected")}
                        variant="outline"
                        className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => convertApplicationToCoach(selectedApplication)}
                    variant="outline"
                    className="rounded-full"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add as Coach
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setApplicationDialogOpen(false)}
                    className="rounded-full ml-auto"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Preferred Applications Tab */}
        {activeTab === "preferred" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#E09F7D]" />
                  Preferred Coach Applications
                </CardTitle>
              </CardHeader>
              <CardContent>
                {preferredApplications.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coach Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preferredApplications.map((app) => (
                        <TableRow key={app.id}>
                          <TableCell className="font-medium">{app.coach_name}</TableCell>
                          <TableCell>{app.coach_email}</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                app.status === "approved" ? "bg-green-500" :
                                app.status === "rejected" ? "bg-red-500" :
                                app.status === "interview_requested" ? "bg-[#E09F7D]" :
                                app.status === "interview_completed" ? "bg-blue-500" :
                                "bg-[#7C9A92]"
                              }
                            >
                              {app.status.replace(/_/g, " ")}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(app.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewPreferredApplication(app)}
                              className="rounded-full"
                              data-testid={`view-preferred-${app.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-[#78716C]">
                    <Crown className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>No preferred coach applications yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Preferred Application Review Dialog */}
        <Dialog open={preferredDialogOpen} onOpenChange={setPreferredDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                <Crown className="w-6 h-6 text-[#E09F7D]" />
                Preferred Coach Application
              </DialogTitle>
            </DialogHeader>
            {selectedPreferredApp && (
              <div className="space-y-6 mt-4">
                {/* Header Info */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-lg text-[#2C332B]">{selectedPreferredApp.coach_name}</h3>
                    <p className="text-sm text-[#78716C]">{selectedPreferredApp.coach_email}</p>
                  </div>
                  <Badge
                    className={
                      selectedPreferredApp.status === "approved" ? "bg-green-500" :
                      selectedPreferredApp.status === "rejected" ? "bg-red-500" :
                      selectedPreferredApp.status === "interview_requested" ? "bg-[#E09F7D]" :
                      "bg-[#7C9A92]"
                    }
                  >
                    {selectedPreferredApp.status.replace(/_/g, " ").toUpperCase()}
                  </Badge>
                </div>

                {/* Doctrinal Affirmation */}
                <div className="bg-[#F5F1E8] rounded-xl p-4">
                  <h4 className="font-medium text-[#2C332B] mb-2">Doctrinal Conviction</h4>
                  <p className="text-sm flex items-center gap-2">
                    {selectedPreferredApp.application_data.affirms_apostles_creed ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-700">Affirms the Doctrinal Statement</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-red-700">Does not affirm</span>
                      </>
                    )}
                  </p>
                  {selectedPreferredApp.application_data.doctrinal_statement && (
                    <p className="text-sm text-[#78716C] mt-2 italic">
                      "{selectedPreferredApp.application_data.doctrinal_statement}"
                    </p>
                  )}
                </div>

                {/* Ethics & Safety */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h4 className="font-medium text-[#2C332B] mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#7C9A92]" />
                    Ethics & Client Safety
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-[#78716C] block">Safety Commitment:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.ethics_commitment}</p>
                    </div>
                    <div>
                      <span className="text-[#78716C] block">Boundary Approach:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.boundary_approach}</p>
                    </div>
                    <div>
                      <span className="text-[#78716C] block">Conflict Resolution:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.conflict_resolution}</p>
                    </div>
                    <div>
                      <span className="text-[#78716C] block">Confidentiality Practices:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.confidentiality_practices}</p>
                    </div>
                  </div>
                </div>

                {/* References */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h4 className="font-medium text-[#2C332B] mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#7C9A92]" />
                    Professional References
                  </h4>
                  <div className="space-y-4">
                    {selectedPreferredApp.application_data.references?.map((ref, i) => (
                      <div key={i} className="p-3 bg-stone-50 rounded-lg">
                        <p className="font-medium text-[#2C332B]">{ref.name}</p>
                        <p className="text-sm text-[#78716C]">{ref.relationship}</p>
                        <div className="flex gap-4 mt-2 text-sm">
                          <a href={`mailto:${ref.email}`} className="text-[#7C9A92] hover:underline flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {ref.email}
                          </a>
                          <a href={`tel:${ref.phone}`} className="text-[#7C9A92] hover:underline flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {ref.phone}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Client Outcomes */}
                <div className="bg-white rounded-xl p-4 border border-stone-100">
                  <h4 className="font-medium text-[#2C332B] mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-[#E09F7D]" />
                    Client Outcomes
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-[#78716C] block">Typical Goals:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.typical_goals}</p>
                    </div>
                    <div>
                      <span className="text-[#78716C] block">Success Stories:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.success_stories}</p>
                    </div>
                    <div>
                      <span className="text-[#78716C] block">Follow-up Approach:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.follow_up_approach}</p>
                    </div>
                    <div>
                      <span className="text-[#78716C] block">Measurement Methods:</span>
                      <p className="mt-1">{selectedPreferredApp.application_data.measurement_methods}</p>
                    </div>
                  </div>
                </div>

                {/* Interview Notes */}
                {(selectedPreferredApp.status === "interview_requested" || selectedPreferredApp.status === "interview_completed") && (
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Interview Notes
                    </h4>
                    <Textarea
                      placeholder="Add notes from the video interview..."
                      value={preferredNotes || selectedPreferredApp.interview_notes || ""}
                      onChange={(e) => setPreferredNotes(e.target.value)}
                      className="min-h-[80px] bg-white"
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t">
                  {selectedPreferredApp.status === "pending_review" && (
                    <>
                      <Button
                        onClick={() => updatePreferredStatus(selectedPreferredApp.id, "interview_requested")}
                        className="rounded-full bg-[#E09F7D] hover:bg-[#d08a68]"
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Request Interview
                      </Button>
                      <Button
                        onClick={() => updatePreferredStatus(selectedPreferredApp.id, "rejected")}
                        variant="outline"
                        className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  {selectedPreferredApp.status === "interview_requested" && (
                    <Button
                      onClick={() => updatePreferredStatus(selectedPreferredApp.id, "interview_completed")}
                      className="rounded-full bg-blue-500 hover:bg-blue-600"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Interview Complete
                    </Button>
                  )}
                  {selectedPreferredApp.status === "interview_completed" && (
                    <>
                      <Button
                        onClick={() => updatePreferredStatus(selectedPreferredApp.id, "approved")}
                        className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => updatePreferredStatus(selectedPreferredApp.id, "rejected")}
                        variant="outline"
                        className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => setPreferredDialogOpen(false)}
                    className="rounded-full ml-auto"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminPage;
